import * as React from 'react';
import { GenericChartComponent, getMouseCanvas } from '@react-financial-charts/core';
import type { MoreProps, PositionType } from '@/types/commontypes';
import { COLORS } from '@/constants/colors';
import {
  roundedRect,
  drawCancelIcon,
  measureTextWidth,
  BOX_HEIGHT,
  BOX_RADIUS,
  FONT_NORMAL,
  FONT_BOLD,
} from '@/utils/canvasUtils';

// ─── 레이아웃 상수 ────────────────────────────────────────────────
const RADIUS = BOX_RADIUS;
const ICON_SIZE = 14;
const GAP = 6; // 요소 간격 (auto layout)
const H_PAD = 4; // 전체 박스 좌우 내부 여백
const ITEM_PAD = 3; // market/amount 박스 상하 여백
const TEXT_PAD = 6; // market/amount 박스 좌우 여백

const NORMAL_FONT = FONT_NORMAL;
const BOLD_FONT = FONT_BOLD;

/**
 * ctx 없이 렌더 전에 OrderEntryBox 전체 너비를 계산한다.
 * EachInteractiveOrderLine의 render()에서 rightOffset 도출에 사용.
 */
export function calcEntryBoxWidth(amount: number, positionType: 'long' | 'short'): number {
  const labelText = positionType === 'long' ? 'Long' : 'Short';
  const labelW = measureTextWidth(labelText, BOLD_FONT) + 6 + 2;
  const amountStr = amount > 0 ? amount.toLocaleString() : '5,000';
  const marketBoxW = measureTextWidth('market', NORMAL_FONT) + TEXT_PAD * 2;
  const amountBoxW = measureTextWidth(amountStr, NORMAL_FONT) + TEXT_PAD * 2;
  return 2 + labelW + GAP + marketBoxW + GAP + amountBoxW + GAP + ICON_SIZE + H_PAD;
}

// ─── Props ────────────────────────────────────────────────────────
export interface OrderEntryBoxProps {
  readonly yValue: number;
  readonly positionType: PositionType;
  readonly amount: number;
  readonly isDark: boolean;
  readonly selected: boolean;
  readonly draggable: boolean;
  readonly rightOffset: number; // 박스 오른쪽 끝이 차트 오른쪽에서 얼마나 떨어져 있는지
  readonly isEntered?: boolean; // 체결 상태: 배경을 포지션 색으로 채움

  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDelete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onSettingsClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class OrderEntryBox extends React.Component<OrderEntryBoxProps> {
  static defaultProps = {
    selected: false,
    draggable: false,
    rightOffset: 60,
  };

  public boxX = 0;
  public boxY = 0;

  render() {
    const { onHover, onUnHover, onDragStart, onDrag, onDragComplete, draggable } = this.props;

    const dragProps = draggable ? { onDragStart, onDrag, onDragComplete } : {};

    return (
      <GenericChartComponent
        clip={true}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        isHover={this.isHover}
        enableDragOnHover
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onHover={onHover}
        onUnHover={onUnHover}
        onClick={this.handleClick}
        {...dragProps}
      />
    );
  }

  private closeX = 0;
  private hasBeenDrawn = false; // drawOnCanvas 최초 실행 전 클릭 이벤트 차단용
  public totalW = 158; // isHover 히트테스트용 — drawOnCanvas에서 갱신됨

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    ctx.save();
    const { yValue, positionType, amount, isDark, rightOffset, isEntered } = this.props;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;

    const y = Math.round(yScale(yValue));
    if (y < 0 || y > height) {
      ctx.restore();
      return;
    }

    const color = positionType === 'long' ? COLORS.long : COLORS.short;

    // ─── 1. 각 요소 너비 측정 (ctx로 재측정 — 폰트 실측값) ────────
    ctx.textBaseline = 'middle';
    ctx.font = BOLD_FONT;
    const labelText = positionType === 'long' ? 'Long' : 'Short';
    const labelW = ctx.measureText(labelText).width + 6 + 2;

    ctx.font = NORMAL_FONT;
    const amountStr = amount > 0 ? amount.toLocaleString() : '5,000';
    const marketBoxW = ctx.measureText('market').width + TEXT_PAD * 2;
    const amountBoxW = ctx.measureText(amountStr).width + TEXT_PAD * 2;

    const totalW = 2 + labelW + GAP + marketBoxW + GAP + amountBoxW + GAP + ICON_SIZE + H_PAD;
    this.totalW = totalW;

    // rightOffset: 헷지 숏 박스는 부모가 미리 계산한 고정값으로 boxX를 결정
    const boxX = width - totalW - rightOffset;
    const boxY = y - BOX_HEIGHT / 2;
    this.boxX = boxX;
    this.boxY = boxY;

    // ─── 2. 각 요소 x 좌표 계산 ─────────────────────────────────
    let cursor = boxX + 2;
    cursor += labelW + GAP;

    const marketX = cursor;
    const marketCx = cursor + marketBoxW / 2;
    cursor += marketBoxW + GAP;

    const amountX = cursor;
    const amountCx = cursor + amountBoxW / 2;
    cursor += amountBoxW + GAP;

    // const settingsCx = cursor + ICON_SIZE / 2; // 설정 아이콘 중앙 X (미사용)
    // this.settingsX = cursor; // 설정 아이콘 왼쪽 끝 X (미사용)
    // cursor += ICON_SIZE + GAP;

    const closeCx = cursor + ICON_SIZE / 2; // X(삭제) 아이콘 중앙 X
    this.closeX = cursor; // X(삭제) 아이콘 왼쪽 끝 X (클릭 영역 판별용)

    // ─── 3. 배경 & 테두리 ───────────────────────────────────────
    const bgColor = isEntered ? color : isDark ? '#1f1f1f' : '#FFFFFF';
    const borderColor = isEntered ? (isDark ? '#1f1f1f' : '#FFFFFF') : color;
    const textColor = isEntered ? (isDark ? '#1f1f1f' : '#FFFFFF') : color;

    ctx.lineWidth = 1;
    ctx.strokeStyle = borderColor;

    roundedRect(ctx, boxX, boxY, totalW, BOX_HEIGHT, RADIUS);
    ctx.fillStyle = bgColor;
    ctx.fill();
    ctx.stroke(); // path 재사용 (fill 후 stroke)

    // market / amount 섹션 박스 (상하 ITEM_PAD, 좌우는 이미 TEXT_PAD 포함)
    roundedRect(ctx, marketX, boxY + ITEM_PAD, marketBoxW, BOX_HEIGHT - ITEM_PAD * 2, RADIUS);
    ctx.stroke();
    roundedRect(ctx, amountX, boxY + ITEM_PAD, amountBoxW, BOX_HEIGHT - ITEM_PAD * 2, RADIUS);
    ctx.stroke();

    // ─── 4. 텍스트 & 아이콘 ─────────────────────────────────────
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.font = BOLD_FONT;
    ctx.fillText(labelText, boxX + 2 + 6, y); // boxX + 좌측시작(2) + 좌패딩(6)

    ctx.textAlign = 'center';
    ctx.font = NORMAL_FONT;
    ctx.fillText('market', marketCx, y);
    ctx.fillText(amountStr, amountCx, y);

    // drawIcon(ctx, SETTINGS_PATH_DATA, settingsCx, y, ICON_SIZE, color); // 설정 아이콘 미사용
    drawCancelIcon(ctx, closeCx, y, textColor);
    this.hasBeenDrawn = true;
    ctx.restore();
  };

  private readonly isHover = (moreProps: MoreProps) => {
    const { onHover } = this.props;
    if (!onHover) return false;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;
    return (
      mouseX >= this.boxX &&
      mouseX <= this.boxX + this.totalW &&
      mouseY >= this.boxY &&
      mouseY <= this.boxY + BOX_HEIGHT
    );
  };

  // ✕ 영역 클릭 → onDelete, ⚙ 영역 클릭 → onSettingsClick
  // GenericComponent는 onClick을 isHover 여부와 무관하게 항상 전파하므로
  // 박스 영역 밖 클릭을 직접 걸러야 한다 (GenericComponent.js:108 참고)
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const {
      mouseXY: [mouseX, mouseY], // mouseXY는 ChartCanvas.mutableState 초기값이 [0,0]이므로 onClick 시점에 항상 정의됨 — undefined 방어 불필요
    } = moreProps;
    if (mouseX < this.boxX || mouseX > this.boxX + this.totalW || mouseY < this.boxY || mouseY > this.boxY + BOX_HEIGHT)
      return;
    if (!this.hasBeenDrawn) return; // drawOnCanvas 실행 전이면 좌표가 유효하지 않으므로 무시
    if (mouseX >= this.closeX) {
      this.props.onDelete?.(e, moreProps);
    }
    // else if (mouseX >= this.settingsX) { // ⚙ 설정 아이콘 — 추후 구현 예정, 현재 미사용
    //   this.props.onSettingsClick?.(e, moreProps);
    // }
  };
}
